import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PlatformAccessGuard } from 'app/core/auth/platform-access/platform-access.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { UnityViewerComponent } from './modules/project/components/unity-viewer/unity-viewer.component';

// @formatter:off
/* eslint-disable max-len */
export const appRoutes: Route[] = [
  { path: 'measure', component: UnityViewerComponent },
  // if want to close the app for maintenance un comment this line

  // {
  //   path: '',
  //   component: LayoutComponent,
  //   data: {
  //     layout: 'empty'
  //   },
  //   children: [
  //     {path: '**', pathMatch: 'full', loadChildren: () => import('app/modules/errors/maintenance/maintenance.module').then(m => m.MaintenanceModule)},
  //   ]
  // },

  // Redirect empty path to '/example'
  { path: '', pathMatch: 'full', redirectTo: 'projects/all_projects' },

  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  {
    path: 'signed-in-redirect',
    pathMatch: 'full',
    redirectTo: 'projects/all_projects',
  },
  {
    path: 'projects/my-projects',
    pathMatch: 'full',
    redirectTo: 'projects/all_projects',
  },

  // Standalone Unauthorized page — public, NO guards (prevents redirect loop, FR-009)
  {
    path: 'unauthorized',
    loadChildren: () =>
      import('app/modules/errors/unauthorized/unauthorized.module').then(
        (m) => m.UnauthorizedModule,
      ),
  },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/modules/auth/confirmation-required/confirmation-required.module').then(
            (m) => m.AuthConfirmationRequiredModule,
          ),
      },
      {
        path: 'forgot-password',
        loadChildren: () =>
          import('app/modules/auth/forgot-password/forgot-password.module').then(
            (m) => m.AuthForgotPasswordModule,
          ),
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('app/modules/auth/reset-password/reset-password.module').then(
            (m) => m.AuthResetPasswordModule,
          ),
      },
      {
        path: 'verify-otp',
        loadChildren: () =>
          import('app/modules/auth/verify-otp/verify-otp.module').then((m) => m.VerifyOtpModule),
      },
      {
        path: 'sign-in',
        loadChildren: () =>
          import('app/modules/auth/sign-in/sign-in.module').then((m) => m.AuthSignInModule),
      },
      {
        path: 'sign-up',
        loadChildren: () =>
          import('app/modules/auth/sign-up/sign-up.module').then((m) => m.AuthSignUpModule),
      },
      {
        path: '503',
        loadChildren: () =>
          import('app/modules/errors/maintenance/maintenance.module').then(
            (m) => m.MaintenanceModule,
          ),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () =>
          import('app/modules/auth/sign-out/sign-out.module').then((m) => m.AuthSignOutModule),
      },
      {
        path: 'unlock-session',
        loadChildren: () =>
          import('app/modules/auth/unlock-session/unlock-session.module').then(
            (m) => m.AuthUnlockSessionModule,
          ),
      },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'home',
        loadChildren: () =>
          import('app/modules/landing/home/home.module').then((m) => m.LandingHomeModule),
      },
    ],
  },

  // Admin routes AuthGuarded Modern layout
  {
    path: '',
    canActivate: [PlatformAccessGuard, AuthGuard],
    canActivateChild: [PlatformAccessGuard, AuthGuard],
    component: LayoutComponent,
    children: [
      // {path: 'example', loadChildren: () => import('app/modules/admin/example/example.module').then(m => m.ExampleModule)},
      {
        path: 'projects',
        loadChildren: () =>
          import('app/modules/projects/projects.module').then((m) => m.ProjectsModule),
      },
      {
        path: 'project',
        loadChildren: () =>
          import('app/modules/project/project.module').then((m) => m.ProjectModule),
      },

      {
        path: 'admin',
        loadChildren: () =>
          import('app/modules/super-admin/super-admin.module').then((m) => m.SuperAdminModule),
      },
      {
        path: 'operation',
        loadChildren: () =>
          import('app/modules/operation-section/operation-section.module').then(
            (m) => m.OperationSectionModule,
          ),
      },
      {
        path: 'demo',
        loadChildren: () => import('app/modules/demo/demo.module').then((m) => m.DemoModule),
      },
      {
        path: 'tickets/details/:id',
        redirectTo: 'observations/details/:id',
        pathMatch: 'full', // Ensures exact matching of this URL
      },
      {
        path: 'tickets',
        redirectTo: 'observations',
        pathMatch: 'full', // Redirects `/tickets` to `/observations`
      },
      {
        path: 'observations', // Deprecated
        loadChildren: () =>
          import('app/modules/observations-manager/observations-manager.module').then(
            (m) => m.ObservationsManagerModule,
          ),
      },
      {
        path: 'demo-viewer',
        loadChildren: () =>
          import('app/modules/demo-viewer/demo-Viewer.module').then((m) => m.DemoViewerModule),
      },
      {
        path: 'files', // Deprecated
        loadChildren: () =>
          import('app/modules/files-manager/files-manager.module').then(
            (m) => m.FilesManagerModule,
          ),
      },
      {
        path: 'bim360',
        loadChildren: () =>
          import('app/modules/bim360/bim360-loading/bim360-loading.module').then(
            (m) => m.Bim360LoadingModule,
          ),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('app/modules/settings/settings.module').then((m) => m.SettingsModule),
      },
      {
        path: 'frequently-asked-questions',
        loadChildren: () =>
          import('app/modules/frequently-asked-questions/frequently-asked-questions.module').then(
            (m) => m.FrequentlyAskedQuestionsModule,
          ),
      },
      {
        path: 'help-center',
        loadChildren: () =>
          import('app/modules/help-center/help-center.module').then((m) => m.HelpCenterModule),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('app/modules/notifications/all-notifications.module').then(
            (m) => m.AllNotificationsModule,
          ),
      },
      {
        path: 'print', // Deprecated
        loadChildren: () =>
          import('app/modules/print-project/print-project.module').then(
            (m) => m.PrintProjectModule,
          ),
      },
      {
        path: 'new-project',
        loadChildren: () =>
          import('app/modules/new-project/new-project.module').then((m) => m.NewProjectModule),
      },
      {
        path: 'analytics',
        loadChildren: () =>
          import('app/modules/analytics/analytics.module').then((m) => m.AnalyticsModule),
      },
      {
        path: 'qhse-management',
        loadChildren: () =>
          import('app/modules/qhse-management/qhse-management.module').then(
            (m) => m.QhseManagementModule,
          ),
      },
      {
        path: 'tools',
        loadChildren: () => import('app/modules/tools/tools.module').then((m) => m.ToolsModule),
      },
      {
        path: 'project-d',
        loadChildren: () =>
          import('app/modules/p-dashboard/p-dashboard.module').then((m) => m.PDashboardModule),
      },

      // 404 & Catch all
      {
        path: '403',
        loadChildren: () =>
          import('app/modules/errors/error-unauthorized/error-unauthorized.module').then(
            (m) => m.ErrorUnauthorizedModule,
          ),
      },
      {
        path: '500',
        loadChildren: () =>
          import('app/modules/errors/error-500/error-500.module').then((m) => m.Error500Module),
      },

      {
        path: '**',
        pathMatch: 'full',
        loadChildren: () =>
          import('app/modules/errors/error-404/error-404.module').then((m) => m.Error404Module),
      },
    ],
  },

  // Admin routes Project Only
  {
    path: '',
    canActivate: [PlatformAccessGuard, AuthGuard],
    canActivateChild: [PlatformAccessGuard, AuthGuard],
    component: LayoutComponent,
    data: {
      // layout: 'empty'
    },
    children: [
      {
        path: 'project',
        loadChildren: () =>
          import('app/modules/project/project.module').then((m) => m.ProjectModule),
      },
    ],
  },

  // 404 & Catch all (not used for now )
  {
    path: '**',
    pathMatch: 'full',
    loadChildren: () =>
      import('app/modules/errors/error-404/error-404.module').then((m) => m.Error404Module),
  },
  {
    path: '503',
    loadChildren: () =>
      import('app/modules/errors/maintenance/maintenance.module').then((m) => m.MaintenanceModule),
  },
];
